"use client"

import { useState, useEffect } from "react"
import { Card, Row, Col, Input, Button, Form, Modal, ModalBody, Tooltip } from "reactstrap"
import { FaPlus, FaCheck, FaTrash, FaArrowLeft } from "react-icons/fa"
import { useNavigate, useLocation } from "react-router-dom"
import LoadingOverlay from "react-loading-overlay-ts"
import DNALoader from "../../components/Loaders/DnaLoader"
import Alerts from "../../components/Alerts/alerts"
import DashboardService from "./dashboard.service"
import AuthService from "../Authentication/auth.service"
import { useTranslation } from "react-i18next"
import Select from "react-select"

const OrderConfirmationModal = ({ isOpen, toggle, orderId }) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="order-confirmation-modal">
      <ModalBody className="text-center p-4">
        <div className="confirmation-icon mb-3">
          <FaCheck size={24} color="#fff" />
        </div>
        <h4 className="mb-2">Order Placed</h4>
        <h2 className="order-id mb-0">ID# {orderId}</h2>
      </ModalBody>
    </Modal>
  )
}

const AddNewRFQ = () => {
  const [items, setItems] = useState([])
  const [allItems, setAllItems] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [orderId, setOrderId] = useState("")
  const [loading, setLoading] = useState(false)
  const [selectedItems, setSelectedItems] = useState(new Set())
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [userInfo] = useState(JSON.parse(localStorage.getItem("user")))
  const [Fetched_Businesses, setFetched_Businesses] = useState([])
  const location = useLocation()
  const orderDetails = location.state?.orderDetails
  const [allDoctors, setAllDoctors] = useState([])
  const [doctor, setDoctor] = useState("")
  const [reason_of_use, setReason_of_use] = useState("")
  const [subtotal, setSubtotal] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [tooltipOpen, setTooltipOpen] = useState({})

  useEffect(() => {
    fetchAllItems()
    Fetch_Businesses()
    fetchAllDoctors()
  }, [])

  useEffect(() => {
    if (allItems.length > 0 && orderDetails) {
      prefillOrderDetails()
    }
  }, [allItems, orderDetails])

  useEffect(() => {
    // Calculate subtotal whenever items change
    calculateTotals()
  }, [items])

  const calculateTotals = () => {
    const calculatedSubtotal = items.reduce((sum, item) => {
      return sum + calculateItemTotal(item)
    }, 0)

    setSubtotal(calculatedSubtotal)

    setTotalAmount(calculatedSubtotal)
  }

  function Fetch_Businesses() {
    setLoading(true)
    DashboardService.GetBusinesses().then(
      (response) => {
        setLoading(false)
        if (response.data.message === "Unauthorized") {
          AuthService.logout()
          navigate("/login")
          window.location.reload()
        } else {
          const business = response.data.data[0]
          setFetched_Businesses([business])
        }
      },
      (error) => {
        setLoading(false)
        const errContent = (error.response && error.response.data) || error.message || error.toString()
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."))
      },
    )
  }

  const fetchAllItems = async () => {
    setLoading(true)
    try {
      const getData = {
        functionName: "GetDropDownItems",
        postData: {
          sort: { _id: -1 },
          offset: 0,
          limit: 99999,
          portal: "customers",
          filter: {},
          business: [],
        },
      }
      const response = await DashboardService.getAllItems(getData)
      if (response.data.message === "Unauthorized") {
        AuthService.logout()
        navigate("/login")
      } else {
        // Add a default price to each item and format labels
        const itemsWithPrice = response.data.data.map((item) => ({
          ...item,
          formattedLabel: `${item.item_name} ${item.BaseUOM} (${item.ItemGroup || "NP"})`,
          // defaultPrice: Math.floor(Math.random() * 100) + 10, // Random price between 10 and 110
        }))
        setAllItems(itemsWithPrice)
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching items"))
    }
    setLoading(false)
  }

  const fetchAllDoctors = async () => {
    setLoading(true)
    try {
      const getData = {
        functionName: "GetAllDoctors",
        postData: {},
      }
      const response = await DashboardService.GetAllDoctors(getData)
      if (response.data.message === "Unauthorized") {
        AuthService.logout()
        navigate("/login")
      } else {
        setAllDoctors(response.data.data)
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching items"))
    }
    setLoading(false)
  }

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: null,
        quantity: 1,
        price: 0,
        discount: 0,
      },
    ])
  }

  const handleItemChange = async (index, key, value) => {
    const newItems = [...items]
    const oldItem = newItems[index]

    if (key === "name") {
      // Set initial price when an item is selected
      newItems[index] = {
        ...oldItem,
        [key]: value,
        category: value.category,
        price: value.price || 0,
      }

      setSelectedItems((prev) => {
        const updated = new Set(prev)
        if (oldItem.name) {
          updated.delete(oldItem.name)
        }
        if (value) {
          updated.add(value)
        }
        return updated
      })
    } else {
      newItems[index] = { ...oldItem, [key]: value }
    }

    setItems(newItems)
  }

  const handleDoctor = (selectedOption) => {
    setDoctor(selectedOption)
  }

  const handleRemoveItem = (index) => {
    const removedItem = items[index]
    const newItems = items.filter((_, i) => i !== index)
    setItems(newItems)

    if (removedItem.name) {
      setSelectedItems((prev) => {
        const updated = new Set(prev)
        updated.delete(removedItem.name)
        return updated
      })
    }
  }

  const toggleTooltip = (index) => {
    setTooltipOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const prefillOrderDetails = async () => {
    if (orderDetails && orderDetails.items) {
      const prefillItems = await Promise.all(
        orderDetails.items.map(async (item) => {
          const prefillItem = {
            category: item.category,
            name: item.item,
            quantity: item.quantity,
            ItemGroup: item.ItemGroup,
            status: item.status,
            price: item.price || Math.floor(Math.random() * 100) + 10, // Use existing price or random
            discount: item.discount || 0,
          }
          return prefillItem
        }),
      )

      setItems(prefillItems)

      const newSelectedItems = new Set(prefillItems.map((item) => item.name))
      setSelectedItems(newSelectedItems)
      setReason_of_use(orderDetails.reason_of_use)

      // Calculate totals after prefilling
      calculateTotals()
    }
  }

  const saveRFQData = async (rfqData) => {
    try {
      const response = await DashboardService.AddQuotationByAdmin(rfqData)
      if (response.data.message === "Unauthorized") {
        AuthService.logout()
        navigate("/login")
      } else {
        return response.data
      }
    } catch (error) {
      console.error("Error saving Order:", error)
      throw error
    }
  }

  const calculateItemTotal = (item) => {
    const baseTotal = (item.price || 0) * (item.quantity || 0)
    const discountAmount = baseTotal * ((item.discount || 0) / 100)
    return baseTotal - discountAmount
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (doctor === "") {
      return Alerts.swalErrorAlert(t("Please Select a doctor"))
    }
    
    if (items.length === 0) {
      return Alerts.swalErrorAlert(t("At least select 1 item."))
    }

    setLoading(true)
    try {
      const RFQ_ID = Math.floor(100000000 + Math.random() * 900000000)

      const rfqData = {
        items: items.map((item) => ({
          category: item.category,
          item: item.name.value,
          quantity: Number.parseInt(item.quantity),
          ItemGroup: item.name.ItemGroup,
          status: item.name.status,
          price: Number.parseFloat(item.price),
          discount: Number.parseFloat(item.discount || 0),
          item_total: calculateItemTotal(item),
        })),
        reason_of_use: reason_of_use === "" ? 'Treatment of Patient' :reason_of_use,
        user_id: doctor.value,
        rfq_id: RFQ_ID,
        business_id: Fetched_Businesses[0]._id,
        status: "accepted",
        subtotal: subtotal.toFixed(2),
        total_amount: totalAmount.toFixed(2),
      }

      await saveRFQData(rfqData)
      Alerts.swalSuccessAlert(t("Order submitted successfully"))
      navigate("/Quotations")
    } catch (error) {
      Alerts.swalErrorAlert(t("Error submitting Order"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="app-main__outer">
      <div className="container mt-4">
        <div className="mb-4">
          <Button color="link" className="p-0 text-decoration-none" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" />
            <span className="text-dark fw-bold">{t("New Order")}</span>
          </Button>
        </div>
        <Card className="rfq-card">
          <div className="p-4">
            <h4 className="mb-4 fw-bold">{orderDetails ? t("Re New Order") : t("Add New Order")}</h4>
            <Form onSubmit={handleSubmit}>
              <LoadingOverlay active={loading} spinner={<DNALoader />}>
                <Col md={12}>
                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <h5 className="fw-bold">{t("Select Doctor")}</h5>
                    </Col>
                    <Col md={4}>
                      <Select
                        value={doctor}
                        onChange={(selectedOption) => handleDoctor(selectedOption)}
                        options={allDoctors.map((doc) => ({
                          value: doc._id,
                          label: doc.name,
                        }))}
                        placeholder={t("Select Doctor")}
                        isSearchable
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <h5 className="fw-bold">{t("Reason of Use")}</h5>
                    </Col>
                    <Col md={4}>
                      <Input
                        type="text"
                        value={reason_of_use}
                        onChange={(e) => setReason_of_use(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Col>
                <h5 style={{ marginTop: "10px" }} className="mb-3 fw-bold">
                  {t("Add Item(s)")}
                </h5>
                <div className="item-list mb-3">
                  <Row className="mb-2 text-muted">
                    <Col md={3}>{t("Item(s)")}</Col>
                    <Col md={1}>{t("Quantity")}</Col>
                    <Col md={1}>{t("UOM")}</Col>
                    <Col md={2}>{t("Price (RM)")}</Col>
                    <Col md={2}>{t("Discount %")}</Col>
                    <Col md={2}>{t("Total")}</Col>
                    <Col md={1} className="text-end">
                      {t("Action")}
                    </Col>
                  </Row>
                  {items.map((item, index) => (
                    <Row key={index} className="mb-2 align-items-center">
                      {/* Item Selection */}
                      <Col md={3}>
                        <div id={`tooltip-${index}`}>
                          <Select
                            value={item.name}
                            onChange={(selectedOption) => handleItemChange(index, "name", selectedOption)}
                            options={allItems.map((catItem) => ({
                              category: catItem.category,
                              value: catItem._id,
                              label: catItem.item_name,
                              ItemGroup: catItem.ItemGroup,
                              item_code: catItem.item_code,
                              price: catItem.price,
                              BaseUOM: catItem.BaseUOM,
                              isDisabled:
                                catItem.status !== "Active" ||
                                (selectedItems.has(catItem.value) && item.name?.value !== catItem.value),
                            }))}
                            placeholder={t("Select Item")}
                            isSearchable
                          />
                        </div>
                        {item.name && (
                          <Tooltip
                            placement="top"
                            isOpen={tooltipOpen[index] || false}
                            target={`tooltip-${index}`}
                            toggle={() => toggleTooltip(index)}
                          >
                            {allItems.find(catItem => catItem._id === item.name.value)?.formattedLabel || item.name.label}
                          </Tooltip>
                        )}
                      </Col>

                      {/* Quantity Selection */}
                      <Col md={1}>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, "quantity", Number.parseInt(e.target.value) || 1)}
                        />
                      </Col>

                      {/* UOM Display */}
                      <Col md={1}>
                        <Input
                          type="text"
                          value={item.name?.BaseUOM || ''}
                          readOnly
                          className="form-control-plaintext"
                        />
                      </Col>

                      {/* Price Selection */}
                      <Col md={2}>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price || 0}
                          onChange={(e) => handleItemChange(index, "price", Number.parseFloat(e.target.value) || 0)}
                        />
                      </Col>

                      {/* Discount Selection */}
                      <Col md={2}>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={item.discount || 0}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "discount",
                              Math.min(Math.max(Number.parseFloat(e.target.value) || 0, 0), 100),
                            )
                          }
                        />
                      </Col>

                      {/* Item Total */}
                      <Col md={2}>{calculateItemTotal(item).toFixed(2)}</Col>

                      {/* Remove Item Button */}
                      <Col md={1} className="text-end">
                        <Button color="link" className="p-0 text-danger" onClick={() => handleRemoveItem(index)}>
                          <FaTrash />
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>

                {/* Order Summary Section */}
                <div className="order-summary mb-4">
                  <div className="p-3 border rounded">
                    <h5 className="mb-3 fw-bold">{t("Order Summary")}</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <span>{t("Subtotal")}:</span>
                      <span>RM {subtotal.toFixed(2)}</span>
                    </div>

                    <hr />
                    <div className="d-flex justify-content-between fw-bold">
                      <span>{t("Total")}:</span>
                      <span>RM {totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="d-flex">
                  <Button type="button" color="light" className="me-2 px-3" onClick={handleAddItem}>
                    <FaPlus className="me-2" /> {t("Add New")}
                  </Button>
                  <Button disabled={loading}  type="submit" color="primary" className="px-3">
                    <FaCheck className="me-2" /> {t("Submit")}
                  </Button>
                </div>
              </LoadingOverlay>
            </Form>
          </div>
        </Card>
        <OrderConfirmationModal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} orderId={orderId} />
      </div>
      <style>{`
        .app-main__outer {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding-top: 20px;
        }
        .rfq-card {
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          border: none;
        }
        .form-select, .form-control {
          border-color: #e0e0e0;
          border-radius: 5px;
          padding: 10px;
        }
        .btn-light {
          background-color: #ffffff;
          border-color: #e0e0e0;
          color: #333;
        }
        .btn-primary {
          background-color: #3D607A;
          border-color: #3D607A;
        }
        .text-muted {
          color: #6c757d !important;
        }
        h4, h5 {
          color: #333;
        }
        .item-list {
          border-top: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
          padding: 20px 0;
        }
        .order-confirmation-modal {
          display: flex !important;
          align-items: center;
          justify-content: center;
        }
        .order-confirmation-modal .modal-content {
          border-radius: 20px;
          border: none;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        .order-confirmation-modal .modal-body {
          padding: 2rem;
        }
        .confirmation-icon {
          width: 60px;
          height: 60px;
          background-color: #4CAF50;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }
        .order-confirmation-modal h4 {
          font-size: 1.25rem;
          color: #333;
          margin-bottom: 0.5rem;
        }
        .order-id {
          font-size: 1.5rem;
          font-weight: bold;
          color: #000;
        }
        .order-summary {
          margin-top: 20px;
        }
        .tooltip {
          font-size: 12px;
        }
        .tooltip-inner {
          max-width: 300px;
          text-align: left;
          background-color: #333;
          color: #fff;
          border-radius: 4px;
          padding: 8px 12px;
        }
      `}</style>
    </div>
  )
}

export default AddNewRFQ

